import Dexie, { type Table } from 'dexie';
import type { AIInsight } from '@/stores/har-store';

export interface CachedInsight {
  id: string; // hash of HAR content
  fileName: string;
  insights: AIInsight[];
  timestamp: number;
  viewMode: 'advanced' | 'basic';
}

export interface PendingAnalysis {
  id: string;
  fileName: string;
  summary: unknown;
  viewMode: 'advanced' | 'basic';
  timestamp: number;
  retryCount: number;
}

class AIInsightsDatabase extends Dexie {
  cachedInsights!: Table<CachedInsight>;
  pendingAnalysis!: Table<PendingAnalysis>;

  constructor() {
    super('AIInsightsDB');
    this.version(1).stores({
      cachedInsights: 'id, fileName, timestamp',
      pendingAnalysis: 'id, fileName, timestamp, retryCount',
    });
  }
}

export const db = new AIInsightsDatabase();

// Generate a hash from HAR content for cache key
export async function generateHarHash(content: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(content);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

// Cache insights for a HAR file
export async function cacheInsights(
  harHash: string,
  fileName: string,
  insights: AIInsight[],
  viewMode: 'advanced' | 'basic'
): Promise<void> {
  await db.cachedInsights.put({
    id: harHash,
    fileName,
    insights,
    timestamp: Date.now(),
    viewMode,
  });
}

// Get cached insights for a HAR file
export async function getCachedInsights(
  harHash: string
): Promise<CachedInsight | undefined> {
  return await db.cachedInsights.get(harHash);
}

// Clear old cached insights (older than 30 days)
export async function clearOldCachedInsights(): Promise<void> {
  const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
  await db.cachedInsights.where('timestamp').below(thirtyDaysAgo).delete();
}

// Add analysis to pending queue
export async function queuePendingAnalysis(
  harHash: string,
  fileName: string,
  summary: unknown,
  viewMode: 'advanced' | 'basic'
): Promise<void> {
  await db.pendingAnalysis.put({
    id: harHash,
    fileName,
    summary,
    viewMode,
    timestamp: Date.now(),
    retryCount: 0,
  });
}

// Get all pending analyses
export async function getPendingAnalyses(): Promise<PendingAnalysis[]> {
  return await db.pendingAnalysis.toArray();
}

// Remove a pending analysis
export async function removePendingAnalysis(harHash: string): Promise<void> {
  await db.pendingAnalysis.delete(harHash);
}

// Increment retry count
export async function incrementRetryCount(harHash: string): Promise<void> {
  const analysis = await db.pendingAnalysis.get(harHash);
  if (analysis) {
    await db.pendingAnalysis.update(harHash, {
      retryCount: analysis.retryCount + 1,
    });
  }
}

// Clear all pending analyses
export async function clearPendingAnalyses(): Promise<void> {
  await db.pendingAnalysis.clear();
}
